import { datadogRum } from '@datadog/browser-rum';
import { Grid, Typography } from '@mui/material';
import { CardSection, CardSubSection } from '@side/pantry';
import { get } from 'lodash';
import { type FocusEvent, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAvailableMls } from 'src/common/hooks/useAvailableMls/useAvailableMls';
import { MlsSearchStatus } from 'src/common/hooks/useMlsSearch/types';
import { useMlsSearch } from 'src/common/hooks/useMlsSearch/useMlsSearch';
import { useMatchListingByMls } from 'src/components/AddressInput/StreetAutocomplete/useMatchListingByMls';
import { FormTextField } from 'src/components/Inputs/FormTextField';
import { MlsVendorAutocomplete } from 'src/components/MlsVendorAutocomplete';
import type { InteractionType } from '@/common/types/interaction';
import {
  TrackCoverSheetTaggingEvent,
  useTrackCoverSheetAnalytics,
} from '$analytics/useTrackAnalytics/domains/coverSheet';
import {
  TrackInteractionsEvent,
  useTrackInteractionsAnalytics,
} from '$analytics/useTrackAnalytics/domains/interactions';
import {
  hasNonAlphanumeric,
  removeNonAlphanumeric,
} from '../Coversheet/utils/alphanumeric';
import { MLSAutofillButton } from './MLSAutofillButton';
import type { FormData } from './types';

type MLSAutofillValue = {
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  zip?: string | null;
  county?: string | null;
  state?: string | null;
};

interface Props {
  type: InteractionType;
  handleAddressAutofill: (val: MLSAutofillValue) => void;
  /**
   * Called when the entered MLS vendor + number matches one of the agent's
   * listings. Providing it enables listing matching. `runAutofill` performs the
   * normal MLS autofill and is invoked by the caller if the agent rejects the
   * matched listing.
   */
  onListingMatch?: (
    listingId: string,
    runAutofill: () => Promise<void>,
  ) => void;
}

export const MLSForm = ({
  type,
  handleAddressAutofill,
  onListingMatch,
}: Props) => {
  const listingMatchEnabled = !!onListingMatch;
  const matchListingByMls = useMatchListingByMls();
  const [isMLSAutofillError, setIsMLSAutofillError] = useState(false);
  const [mlsHintText, setMlsHintText] = useState('');
  const { mlsVendorOptions, loading: mlsLoading, error } = useAvailableMls();
  const { setError, setValue, getValues } = useFormContext<FormData>();
  useEffect(() => {
    if (error !== undefined) {
      setError('root.mlsError', { type: 'custom', message: '' });
    }
  }, [error, setError]);
  const [mlsVendor, mlsNumber] = useWatch<FormData, ['mlsVendor', 'mlsNumber']>(
    { name: ['mlsVendor', 'mlsNumber'] },
  );
  const { representationType } = getValues();
  const trackEvent = useTrackCoverSheetAnalytics();

  useEffect(() => {
    const vendor = mlsVendorOptions.find((option) => option.id === mlsVendor);

    if (vendor) {
      setValue('mlsServiceId', vendor.id);
    }

    // A cloned interaction can carry a vendor id (e.g. a legacy preferred-MLS
    // id) that is not in the identity catalog options; clear it so the field
    // doesn't hold an invisible stale selection.
    if (!mlsLoading && mlsVendor && !vendor) {
      setValue('mlsVendor', '');
      setValue('mlsServiceId', '');
    }

    setMlsHintText(vendor?.listingIdTip ?? '');
  }, [mlsVendor, mlsVendorOptions, mlsLoading, setValue]);

  const [loading, setLoading] = useState(false);
  const searchMLS = useMlsSearch();

  if (mlsNumber && hasNonAlphanumeric(mlsNumber)) {
    setValue('mlsNumber', removeNonAlphanumeric(mlsNumber));
  }

  const analyticsObject = {
    type: `${type}s`,
    eventContainer: `${type}_address_card`,
    mls_vendor: mlsVendor,
    mls_number: mlsNumber,
    representationType,
  };

  const trackInteractionsEvent = useTrackInteractionsAnalytics();

  const runMlsAutofill = async () => {
    setLoading(true);
    setIsMLSAutofillError(false);
    const selectedMlsVendor = mlsVendorOptions?.find(
      (option) => option?.id === mlsVendor,
    )?.providerId;
    try {
      const outcome = await searchMLS(`${selectedMlsVendor}`, `${mlsNumber}`);

      if (outcome.status === MlsSearchStatus.Found) {
        const mlsResponse = outcome.result;
        trackInteractionsEvent(TrackInteractionsEvent.ClickMLSAutofill, {
          ...analyticsObject,
          success: true,
        });

        // track mls data completeness
        trackEvent(TrackCoverSheetTaggingEvent.MLSDataCompleteness, {
          mlsNumber,
          mlsVendor: selectedMlsVendor,
          ...get(mlsResponse, 'property', {}),
          ...get(mlsResponse, 'listingAgent', {}),
          brokerageLicense: get(
            mlsResponse,
            ['listingAgent', 'brokerage', 'licenseNumber'],
            '',
          ),
          brokerageName: get(
            mlsResponse,
            ['listingAgent', 'brokerage', 'name'],
            '',
          ),
          photo: get(mlsResponse, ['photo', '0'], ''),
          view: 'new_interaction',
        });
        // Listed out because `addressAutofillHelper` writes every key it gets,
        // and the MLS payload carries property details beyond the address —
        // including `type`, which collides with the buyer agreement field.
        handleAddressAutofill({
          street1: mlsResponse.property.street1,
          street2: mlsResponse.property.street2,
          city: mlsResponse.property.city,
          zip: mlsResponse.property.zip,
          county: mlsResponse.property.county,
          state: mlsResponse.property.state,
        });
      } else {
        trackInteractionsEvent(TrackInteractionsEvent.ClickMLSAutofill, {
          ...analyticsObject,
          success: false,
        });
        setIsMLSAutofillError(true);
      }
    } catch {
      trackInteractionsEvent(TrackInteractionsEvent.ClickMLSAutofill, {
        ...analyticsObject,
        success: false,
      });
      setIsMLSAutofillError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMlsAutofill = async () => {
    if (listingMatchEnabled && mlsVendor && mlsNumber) {
      setLoading(true);
      setIsMLSAutofillError(false);
      try {
        const matchedListingId = await matchListingByMls(mlsVendor, mlsNumber);
        if (matchedListingId) {
          setLoading(false);
          trackInteractionsEvent(TrackInteractionsEvent.ClickMLSAutofill, {
            ...analyticsObject,
            listingId: matchedListingId,
            success: true,
          });
          onListingMatch?.(matchedListingId, runMlsAutofill);
          return;
        }
      } catch (err) {
        datadogRum.addError(err, {
          message: 'Failed to match listing by MLS vendor and number',
          mlsVendor,
          mlsNumber,
        });
      }
    }

    await runMlsAutofill();
  };

  const handleFieldSelect = (e: FocusEvent<HTMLInputElement>) => {
    // Don't track when select box regains focus after popover menu closes
    if (e.relatedTarget?.matches('[role="option"], .MuiPaper-root')) {
      return;
    }

    const fieldName = e.target.name ?? e.target.id;

    trackInteractionsEvent(TrackInteractionsEvent.SelectField, {
      eventContainer: `${type}_address_card`,
      eventTarget: `location.${fieldName}`,
      selected_input: e.target.value,
      representationType,
    });
  };

  return (
    <CardSection cardTitle="Search property by MLS">
      <CardSubSection sx={{ pt: 3, pb: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Input the MLS number to pre-fill the address and contact information
          on the Cover Sheet
        </Typography>
        <Grid
          container
          rowSpacing={1.25}
          columnSpacing={2}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Grid size={{ xs: 12, lg: 5 }}>
            <MlsVendorAutocomplete
              name="mlsVendor"
              label="MLS Vendor"
              options={mlsVendorOptions}
              loading={mlsLoading}
              error={isMLSAutofillError}
              helperText={
                isMLSAutofillError ? 'MLS autofill failed' : undefined
              }
              fixedHeight
              onFocus={handleFieldSelect}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <FormTextField
              error={isMLSAutofillError}
              name="mlsNumber"
              label="MLS Number"
              fullWidth
              fixedHeight
              onFocus={handleFieldSelect}
              placeholder={mlsHintText}
              disableRequiredHighlight
            />
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6, lg: 3 }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <MLSAutofillButton
              disabled={Boolean(!mlsVendor || !mlsNumber)}
              onClick={() => void handleSubmitMlsAutofill()}
              loading={loading}
            />
          </Grid>
        </Grid>
      </CardSubSection>
    </CardSection>
  );
};
